/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-unused-vars */
import type { DSFastListT } from '@elliemae/ds-fast-list';
import type { DSFloatingWrapperT, useFloatingContext } from '@elliemae/ds-floating-context';
import type { DSMaskT } from '@elliemae/ds-form-helpers-mask-hooks';
import type { DSPopperJST } from '@elliemae/ds-popperjs';
import type { GlobalAttributesT, XstyledProps } from '@elliemae/ds-props-helpers';
import { globalAttributesPropTypes, PropTypes } from '@elliemae/ds-props-helpers';
import type { SizingProps } from '@elliemae/ds-system';
import type React from 'react';

export function noop<T extends unknown[]>(..._args: T): void {}

export declare namespace DSComboboxT {
  type ReturnOfUseFloatingContext = ReturnType<typeof useFloatingContext>;
  export interface CommonItemOptions extends GlobalAttributesT {
    dsId: string;
    disabled?: boolean;
    applyAriaDisabled?: boolean;
    render?: React.ComponentType<unknown>;
  }
  export interface ItemSeparatorOptions extends CommonItemOptions {
    type: 'separator';
  }

  export interface ItemSectionOptions extends CommonItemOptions {
    type: 'section';
    label: string;
    options: OptionTypes[];
  }

  export interface ItemOption extends CommonItemOptions {
    value: string;
    label: string;
    type: 'option';
  }

  export interface ItemCreatableOption extends CommonItemOptions {
    label: string;
    type: 'creatable';
  }

  export type OptionTypes = ItemOption | ItemSectionOptions | ItemSeparatorOptions | ItemCreatableOption;

  export type SelectedOptionsT = ItemOption[] | ItemOption | null;
  export interface PropsOptional {
    menuMinWidth?: SizingProps['minWidth'];
    menuMaxHeight?: SizingProps['maxHeight'];
    placeholder?: string;
    autoFocus?: boolean;
    filteredOptions?: OptionTypes[];
    isMenuOpen?: boolean;
    onMenuChange?: (
      state: boolean,
      reason: string,
      e?: React.KeyboardEvent | React.MouseEvent | React.ChangeEvent<HTMLInputElement>,
    ) => void;
    onCreate?: (label: string) => void;
    onFilter?: (newOptions: OptionTypes[], inputValue?: string) => void;
    onCancel?: () => void;
    onSelectAll?: (suggestedValue: OptionTypes[] | [], event: React.MouseEvent | React.KeyboardEvent) => null;
    onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>, value: ItemOption) => void;
    isLoading?: boolean;
    popperProps?: DSFloatingWrapperT.Props;
  }

  export interface RequiredProps {
    allOptions: OptionTypes[];
    selectedValues: SelectedOptionsT;
    onChange: (
      suggestedValue: SelectedOptionsT,
      selectedOption: ItemOption,
      event: React.MouseEvent | React.KeyboardEvent,
    ) => void;
  }
  export interface DefaultProps {
    startPlacementPreference: DSPopperJST.PopperPlacementsT;
    placementOrderPreference: DSPopperJST.PopperPlacementsT[];
    hasError: boolean;
    inline: boolean;
    withoutPortal: boolean;
    isNonClearable: boolean;
    disabled: boolean;
    noOptionsMessage: string;
    innerRef: React.MutableRefObject<HTMLInputElement | null> | ((ref: HTMLInputElement | null) => void);
    useMask: DSMaskT.Hook | typeof noop;
    onlySelectable: boolean;
    isSkeleton: boolean;
    applyAriaDisabled: boolean;
    readOnly: boolean;
    isMenuOpenableWithReadOnly: boolean;
    forceFocusFirstOptionOnType: boolean;
  }

  export interface Props
    extends Partial<DefaultProps>,
      RequiredProps,
      PropsOptional,
      Omit<GlobalAttributesT, keyof DefaultProps | keyof RequiredProps | keyof PropsOptional>,
      XstyledProps {}

  export interface InternalProps
    extends DefaultProps,
      RequiredProps,
      PropsOptional,
      Omit<GlobalAttributesT, keyof DefaultProps | keyof RequiredProps | keyof PropsOptional>,
      XstyledProps {}

  type ActionRefVirtualized = {
    scrollTo: (index: number, opts?: DSFastListT.ScrollToOptions) => void;
  };
}

export const ComboboxPropTypes = {
  ...globalAttributesPropTypes,
  placeholder: PropTypes.string.description("input's placeholder value").defaultValue(''),
  allOptions: PropTypes.oneOfType([PropTypes.array, PropTypes.object])
    .isRequired.description('List of original options')
    .defaultValue([]),
  filteredOptions: PropTypes.oneOfType([PropTypes.array, PropTypes.object])
    .description('List of filtered options to show in the menu based on the current internal input filter value')
    .defaultValue([]),
  selectedValues: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.object), PropTypes.object]).isRequired.description(
    'Array of option or single option selected',
  ),
  disabled: PropTypes.bool.description('wheter or not the input is disabled').defaultValue('false'),
  autoFocus: PropTypes.bool.description('Whether the combo box uses auto focus or not').defaultValue('false'),
  hasError: PropTypes.bool.description('Whether the combo box has error or not').defaultValue('false'),
  noOptionsMessage: PropTypes.string
    .description('Custome message to be display when no matches found after filtering')
    .defaultValue('No Matches Found'),
  onChange: PropTypes.func.isRequired
    .description('function triggered when an option is selected it will send the options selected')
    .defaultValue('() => {}'),
  onFilter: PropTypes.func.description('function triggered when user type in the combobox input').defaultValue(''),
  onCreate: PropTypes.func
    .description(
      'function triggered when user select "create" a new item whenever is not a match in the all option list',
    )
    .defaultValue(''),
  onCancel: PropTypes.func.description('function triggered when user press ESC key').defaultValue('() => {}'),
  onMenuChange: PropTypes.func
    .description('function triggered when user open or close the menu list')
    .defaultValue('() => {}'),
  menuMinWidth: PropTypes.any.description('Minimum width for the dropdown menu').defaultValue(undefined),
  menuMaxHeight: PropTypes.any.description('Maximum height for the dropdown menu').defaultValue(undefined),
  withoutPortal: PropTypes.bool
    .description('Whether the combobox menu is rendered in a portal or not')
    .defaultValue(false),
  innerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]).description(
    'reference to the component',
  ),
  onlySelectable: PropTypes.bool.description('filtering is disabled'),
  isSkeleton: PropTypes.bool.description('whether or not the component is in skeleton state').defaultValue(false),
  applyAriaDisabled: PropTypes.bool
    .description(
      'Whether to apply disabled styling and announce as disabled in SR. CAN STILL RECEIVE FOCUS. PREVENTS ONLY MAIN ACTION.',
    )
    .defaultValue(false),
  readOnly: PropTypes.bool
    .description('Makes the component non-editable while remaining accessible.')
    .defaultValue(false),
  isMenuOpenableWithReadOnly: PropTypes.bool
    .description('Whether the menu can be opened when the input is readonly')
    .defaultValue(false),
  onSelectAll: PropTypes.func
    .description('function triggered when user select all the options')
    .defaultValue('() => {}')
    .deprecated({ version: '25.2' }),
  popperProps: PropTypes.object
    .description('props to be passed to the FloatingWrapper component')
    .defaultValue(undefined),
  forceFocusFirstOptionOnType: PropTypes.bool
    .description('Whether to focus the first option when typing in the input.')
    .defaultValue(false),
};
